import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { getProducts, getPromoCodes, checkout } from "../utils/api";
import { setJSON } from "../utils/cart";

export default function Checkout({ cart, setCart, currentUser, token }) {
  const navigate = useNavigate();
  const [placing, setPlacing] = useState(false);
  const [err, setErr] = useState("");

  const [products, setProducts] = useState([]);
  const [promos, setPromos] = useState([]);
  const [promoInput, setPromoInput] = useState("");
  const [promoMsg, setPromoMsg] = useState("");
  const [applied, setApplied] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const [prods, codes] = await Promise.all([
          getProducts(),
          getPromoCodes(),
        ]);
        if (cancelled) return;
        setProducts(Array.isArray(prods) ? prods : []);
        setPromos(Array.isArray(codes) ? codes : []);
      } catch {
        if (!cancelled) {
          setProducts([]);
          setPromos([]);
        }
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const byId = new Map((products || []).map((p) => [String(p.id), p]));
  const rawCart = Array.isArray(cart) ? cart : [];
  const cartRows = [];

  for (let i = 0; i < rawCart.length; i++) {
    const c = rawCart[i];
    const qty = Number(c?.quantity || 0);
    if (qty <= 0) continue;

    const p = byId.get(String(c?.id));
    const price = Number(p?.price || 0);

    cartRows.push({
      id: c?.id,
      quantity: qty,
      product: p || null,
      price,
      name: p?.name || `Item #${c?.id}`,
      lineTotal: price * qty,
      status: p?.status || "",
    });
  }

  let subtotal = 0;
  for (let i = 0; i < cartRows.length; i++)
    subtotal += Number(cartRows[i].lineTotal || 0);

  const shippingBase = subtotal > 0 ? 5.0 : 0.0;

  let shipping = shippingBase;
  if (applied && String(applied.type) === "shipping") shipping = 0.0;

  let discount = 0.0;
  if (applied && String(applied.type) === "flat") {
    const amt = Number(applied.amount || 0);
    discount = Math.max(0, Math.min(amt, subtotal));
  }

  const total = Math.max(0, subtotal + shipping - discount);

  function applyPromo() {
    const code = String(promoInput || "")
      .trim()
      .toUpperCase();

    if (!code) {
      setApplied(null);
      setPromoMsg("");
      return;
    }

    const match = (promos || []).find(
      (p) => String(p.code || "").toUpperCase() === code,
    );

    if (!match) {
      setApplied(null);
      setPromoMsg("Invalid promo code");
      return;
    }

    setApplied(match);
    setPromoMsg(`Applied ${match.code}`);
  }

  function clearPromo() {
    setApplied(null);
    setPromoInput("");
    setPromoMsg("");
  }

  async function placeOrder() {
    setErr("");

    if (!token) {
      navigate("/login");
      return;
    }

    if (currentUser?.role !== "buyer" && currentUser?.role !== "admin") {
      setErr("Only buyers can checkout.");
      return;
    }

    const missing = cartRows.find((r) => !r.product);
    if (missing) {
      setErr(
        "Some items in your cart no longer exist. Remove them before checkout.",
      );
      return;
    }
    const notAvailable = cartRows.find(
      (r) => String(r?.product?.status || "").toLowerCase() !== "available",
    );

    if (notAvailable) {
      setErr(
        "Some items are not available anymore. Remove them before checkout.",
      );
      return;
    }

    if (cartRows.length === 0) return;

    const payloadCart = cartRows.map((r) => ({
      id: r.id,
      quantity: r.quantity,
    }));

    const order = {
      orderId: Date.now(),
      createdAt: new Date().toISOString(),
      buyer: currentUser
        ? { id: currentUser.id, name: currentUser.name, role: currentUser.role }
        : null,
      items: cartRows.map((r) => ({
        product_id: r.id,
        product_name: r.name,
        quantity: r.quantity,
        price: r.price,
      })),
      promo: applied
        ? { code: applied.code, type: applied.type, amount: applied.amount }
        : null,
      subtotal,
      shipping,
      discount,
      total,
    };
    setJSON("lastOrder", order);

    try {
      setPlacing(true);

      await checkout(token, payloadCart);

      setCart([]);
      navigate("/my-orders"); // backend orders will show here
    } catch (e) {
      setErr(e?.message || "Checkout failed.");
    } finally {
      setPlacing(false);
    }
  }

  return (
    <div className="container pagePadBottom checkoutPage">
      <div className="pageHeader">
        <div className="pageHeaderLeft">
          <div className="h2">Checkout</div>
          <div className="muted small">
            Review your order and apply promo codes.
          </div>
        </div>

        <div className="pageHeaderRight">
          <div className="pill">
            Total <span className="pillStrong">${total.toFixed(2)}</span>
          </div>
        </div>
      </div>

      {cartRows.length === 0 ? (
        <div className="card emptyCard" style={{ marginTop: 12 }}>
          <div className="emptyTitle">Nothing to checkout</div>
          <div className="muted" style={{ marginTop: 6 }}>
            Your cart is empty.
          </div>
          <div className="row" style={{ marginTop: 12 }}>
            <button
              className="btnPrimary"
              type="button"
              onClick={() => navigate("/categories")}
            >
              Browse items
            </button>
          </div>
        </div>
      ) : (
        <div className="grid2" style={{ marginTop: 12 }}>
          {/* Order summary */}
          <div className="card">
            <div className="cardTitleRow">
              <div className="cardTitle">Order Summary</div>
              <span className="muted small">{cartRows.length} item(s)</span>
            </div>

            <div className="summaryList">
              {cartRows.map((r) => (
                <div key={r.id} className="summaryRow">
                  <div className="summaryLeft">
                    <div className="summaryName">{r.name}</div>
                    <div className="muted small">
                      Qty {r.quantity} • ${r.price.toFixed(2)} each
                    </div>
                    {!r.product ? (
                      <div className="notice warn" style={{ marginTop: 8 }}>
                        This item no longer exists. Total may be inaccurate.
                      </div>
                    ) : null}
                  </div>
                  <div className="summaryValue">${r.lineTotal.toFixed(2)}</div>
                </div>
              ))}
            </div>

            <div className="divider" />

            <div className="summaryRow">
              <div className="muted">Subtotal</div>
              <div className="summaryValue">${subtotal.toFixed(2)}</div>
            </div>

            <div className="summaryRow" style={{ marginTop: 8 }}>
              <div className="muted">Shipping</div>
              <div className="summaryValue">${shipping.toFixed(2)}</div>
            </div>

            <div className="summaryRow" style={{ marginTop: 8 }}>
              <div className="muted">Discount</div>
              <div className="summaryValue">- ${discount.toFixed(2)}</div>
            </div>

            <div className="divider" />

            <div className="summaryRow">
              <div className="summaryTotalLabel">Total</div>
              <div className="priceBlue">${total.toFixed(2)}</div>
            </div>
          </div>

          {/* Promo + buyer */}
          <div className="card">
            <div className="cardTitle">Promo Code</div>
            <div className="muted small" style={{ marginTop: 6 }}>
              Try: SAVE5, SAVE10, FREESHIP
            </div>

            <div className="formRow" style={{ marginTop: 10 }}>
              <input
                className="input"
                value={promoInput}
                placeholder="Enter code"
                onChange={(e) => setPromoInput(e.target.value)}
              />
              <button
                className="btnSecondary"
                onClick={applyPromo}
                type="button"
              >
                Apply
              </button>
            </div>

            {promoMsg ? (
              <div
                className={
                  promoMsg.includes("Invalid") ? "notice warn" : "notice"
                }
                style={{ marginTop: 12 }}
              >
                <div style={{ fontWeight: 900 }}>{promoMsg}</div>
                {applied ? (
                  <div className="muted small" style={{ marginTop: 6 }}>
                    {String(applied.type) === "flat"
                      ? `-$${Number(applied.amount || 0).toFixed(2)}`
                      : "Free shipping"}
                  </div>
                ) : null}

                {applied ? (
                  <div style={{ marginTop: 10 }}>
                    <button
                      className="btnSecondary"
                      type="button"
                      onClick={clearPromo}
                    >
                      Remove promo
                    </button>
                  </div>
                ) : null}
              </div>
            ) : null}

            <div className="divider" />

            <div className="cardTitle">Buyer</div>
            <div className="muted" style={{ marginTop: 6 }}>
              {currentUser
                ? `${currentUser.name} (${currentUser.role})`
                : "Guest checkout"}
            </div>

            <div className="divider" />

            <div style={{ flex: 1 }}>
              {err ? (
                <div className="notice warn" style={{ marginTop: 12 }}>
                  <div style={{ fontWeight: 900 }}>{err}</div>
                </div>
              ) : null}

              <button
                className="btnPrimary"
                style={{ flex: 1 }}
                onClick={placeOrder}
                type="button"
                disabled={placing}
              >
                {placing ? "Placing…" : "Place order"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
